import React, { useEffect, useRef, useState } from "react";
import { useChat } from "../hooks/useChat";
import { useDarkMode } from "../hooks/useDarkMode";
import { useResponsive } from "../hooks/useResponsive";
import { useSidebar } from "../hooks/useSidebar";

import ChatSidebar from "./ui/ChatSidebar";
import ChatHeader from "./ui/ChatHeader";
import ChatArea from "./ui/ChatArea";
import InputArea from "./ui/InputArea";
import ProfileMenu from "./ui/ProfileMenu";

const MODELS = [
  { id: "ozhan-ai", label: "Ozhan AI", endpoint: "/chatbot/" },
  { id: "ozhan-o1", label: "Ozhan O1", endpoint: "/chatbot-o4/" },
];

const Chatbot = () => {
  const { isMobile } = useResponsive();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { sidebarOpen, collapsedSidebar, toggleSidebar, setSidebarOpen } =
    useSidebar(isMobile);

  const [showProfile, setShowProfile] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Persist model selection
  const [modelId, setModelId] = useState(() => {
    return localStorage.getItem("ozhan_chat_model") || MODELS[0].id;
  });

  useEffect(() => {
    localStorage.setItem("ozhan_chat_model", modelId);
  }, [modelId]);

  const activeModel = MODELS.find((m) => m.id === modelId) || MODELS[0];

  // Preferred: pass endpoint to hook
  const { sendMessage } = useChat({ endpoint: activeModel.endpoint, modelId });

  const listRef = useRef(null);

  useEffect(() => {
    if (!isMobile) return;
    setSidebarOpen?.(false);
  }, [isMobile, setSidebarOpen]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, loading]);

  const handleSendMessage = async () => {
    const text = userInput.trim();
    if (!text || loading) return;

    const nextMessages = [...messages, { sender: "user", text }];
    const history = messages.slice(-8).map((m) => ({
      role: m.sender === "user" ? "user" : "assistant",
      content: m.text,
    }));

    setMessages(nextMessages);
    setUserInput("");
    setLoading(true);

    try {
      const reply = await sendMessage(text, history);
      setMessages((prev) => [...prev, { sender: "bot", text: reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "متأسفم، مشکلی پیش آمد." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      className={[
        "min-h-screen font-vazir",
        darkMode ? "dark bg-slate-950" : "bg-slate-50",
      ].join(" ")}
      dir="rtl"
    >
      <ChatSidebar
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        collapsedSidebar={collapsedSidebar}
        onToggleSidebar={toggleSidebar}
        onClose={() => setSidebarOpen?.(false)}
      />

      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40"
          onClick={() => setSidebarOpen?.(false)}
        />
      )}

      <div
        className={[
          "flex flex-col",
          !isMobile ? (collapsedSidebar ? "mr-16" : "mr-72") : "mr-0",
        ].join(" ")}
      >
        <ChatHeader
          isMobile={isMobile}
          onToggleSidebar={toggleSidebar}
          onToggleProfile={() => setShowProfile((p) => !p)}
          modelId={modelId}
          setModelId={setModelId}
          models={MODELS}
        />

        <ProfileMenu
          open={showProfile}
          onClose={() => setShowProfile(false)}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />

        <main className="pt-16">
          <div className="mx-auto max-w-5xl px-3 sm:px-4 lg:px-6 py-4">
            <div className="rounded-3xl bg-white/80 dark:bg-slate-900/60 backdrop-blur ring-1 ring-slate-200 dark:ring-slate-800 shadow-sm overflow-hidden">
              <div className="flex h-[calc(100vh-6.5rem)] flex-col">
                <ChatArea ref={listRef} messages={messages} loading={loading} />
                <InputArea
                  value={userInput}
                  setValue={setUserInput}
                  loading={loading}
                  onSend={handleSendMessage}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Chatbot;
